import React, { useCallback, useEffect, useState } from 'react'
import { Box, Grid } from '@mui/material'
import CurrencyExchangeOutlinedIcon from '@mui/icons-material/CurrencyExchangeOutlined'
import { useStyle } from '../../Style'
import { formatDateLocale } from '../../../../../../../lib/utils.es6'
import SkeletonWrapper from '../../../../../../../containers/NewProductList/components/SkeletonWrapper/SkeletonWrapper'
import { getComplexPolicyRefunds } from '../../../../../../../lib/requests.es6'
import useProductCurrency from '../../../../../../../hooks/useProductCurrency'
import {
  getRefundStatusMeta,
  getRefundTypeLabel,
  getRefundInitiator
} from './refundStatus'

const RefundCard = ({ refund, currencyCode }) => {
  const classes = useStyle()
  const statusMeta = getRefundStatusMeta(refund.status)
  const amount = (Number(refund.amount_in_cents) || 0) / 100

  return (
    <Grid container className={classes.policyWrapper}>
      <Grid item xs={12} sm={12} md={12}>
        <Grid container className={classes.policyHeader}>
          <Grid item xs={12} sm={6} md={6}>
            <Grid container>
              <Grid
                item
                xs={12}
                md={2}
                sm={2}
                className={classes.policyIconWrapper}
              >
                <Box className={classes.policyIcons}>
                  <CurrencyExchangeOutlinedIcon
                    fontSize="14px"
                    sx={{ color: '#ffffff' }}
                  />
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={10}
                sm={10}
                className={classes.policyHeaderContent}
              >
                <h6>{getRefundTypeLabel(refund.refund_type)}</h6>
                <p>{refund.reason || 'Refund'}</p>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            className={classes.policyActiveStatusWrapper}
          >
            <Box display="grid" justifyContent="flex-end">
              <ul>
                <li className={classes[statusMeta.pillClass]}>
                  <span>{statusMeta.label}</span>
                </li>
              </ul>
            </Box>
          </Grid>
          <div className={classes.policyHeaderBorder}></div>
        </Grid>

        <Grid container spacing={2} className={classes.policyContentWrapper}>
          <Grid item xs={12} sm={3} md={3}>
            <p>Requested At</p>
            <h6>{formatDateLocale(refund.created_at) || 'N/A'}</h6>
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <p>Initiated By</p>
            <h6>{getRefundInitiator(refund)}</h6>
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <p>Status Updated At</p>
            <h6>{formatDateLocale(refund.status_updated_at) || 'N/A'}</h6>
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <p>Transaction Reference</p>
            <h6>{refund.partner_reference || 'N/A'}</h6>
          </Grid>
        </Grid>
        <div className={classes.policyContentBorder}></div>
        <Grid container spacing={2} className={classes.policyFooterWrapper}>
          <Grid item xs={12} md={6} sm={6}>
            <p>Status Reason</p>
            <h6>{refund.status_reason || 'N/A'}</h6>
          </Grid>
          <Grid item xs={12} md={6} sm={6}>
            <div className={classes.policyBalance}>
              <p>Amount</p>
              <h6>{`${currencyCode} ${amount.toFixed(2)}`}</h6>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

const PolicyRefunds = props => {
  const classes = useStyle()
  const policyGuid = props.policy && props.policy.guid
  const currencyCode = useProductCurrency(
    props.policy && props.policy.product_code
  )

  const [refunds, setRefunds] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchRefunds = useCallback(() => {
    if (!policyGuid) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    getComplexPolicyRefunds(policyGuid)
      .then(response => {
        setRefunds(Array.isArray(response.data) ? response.data : [])
      })
      .catch(err => {
        setError(
          (err.response && err.response.data && err.response.data.message) ||
            'Could not load refunds for this policy.'
        )
      })
      .finally(() => setLoading(false))
  }, [policyGuid])

  useEffect(() => {
    fetchRefunds()
  }, [fetchRefunds])

  if (loading) {
    return <SkeletonWrapper loading={loading} />
  }

  if (error || refunds.length === 0) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12} className={classes.claimwrapper}>
          <Grid container spacing={2} className={classes.claimdetailswrapper}>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              className={classes.ClaimDetailscontent}
            >
              <p style={{ textAlign: 'center', marginTop: '30px' }}>
                {error || 'No refunds found for this policy'}
              </p>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  return (
    <>
      {refunds.map(refund => (
        <RefundCard
          key={refund.guid}
          refund={refund}
          currencyCode={currencyCode}
        />
      ))}
    </>
  )
}

export default PolicyRefunds
